import {events} from 'lib';
import React from 'react';
import {getErrorCode, getErrorCodeText} from './errorCodes';
//Duplication error when doing a calibration then a scan, store results in separate file
import {setState} from './testConnectState';
import {testConnectSidebarEnabled} from 'features';
import {saveVersion} from 'api/testConnect';

const SOCKET_ADDRESS = 'ws://127.0.0.1:4649/BioLab/';
const SCAN_ERROR_KEY = 'SCAN_ERROR_KEY';

const SOCKET_STATE = {
  OPENING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
};

let _socket;

const identity = () => {};


// Shared state
let state = {
  results: undefined,
  currentStrip: undefined,
  initialStrips: 0,
  strips: [],
  chemicalBaseTypeID: 2,
  currentScanner: undefined,
  url: undefined,
  resolveScannersRequest: identity,
};

const initialState = Object.assign({}, state);


/**
 * Gets the currently open socket, or initializes a new socket connection and returns it.
 * @return {WebSocket} An active socket connection to Test Connect softare.
 */
export function getSocket() {
  if (_socket && _socket.readyState === SOCKET_STATE.OPEN) {
    return Promise.resolve(_socket);
  }


  try {
    _socket = new WebSocket(SOCKET_ADDRESS);
    _socket.onmessage = dataHandler;
    _socket.onerror = errorHandler;
    _socket.onclose = connectionClosedHandler;
  } catch (err) {
    if (!testConnectSidebarEnabled) {
      events.publish('error.user', {message: getErrorCode(11002, getCurrentScanner()), key: SCAN_ERROR_KEY});
    }
  }

  let socketResolver;
  let socketError;

  const maxTimeout = 4000; // Must connect within 4 seconds
  let currentTimeout = 0;

  let openInterval = setInterval(() => {
    if (_socket && _socket.readyState === SOCKET_STATE.OPEN) {
      socketResolver(_socket);
      clearInterval(openInterval);
    }
    currentTimeout += 50;

    if (currentTimeout >= maxTimeout) {
      clearInterval(openInterval);
      socketError('Could not connect to scanner');
    }
  }, 50);

  return new Promise((resolve, reject) => {
    socketResolver = resolve;
    socketError = reject;
  });
}


export function isScanning() {
  return !!state.currentScanner;
}

export function getResults() {
  return state.results;
}

export function getCurrentScanner() {
  return state.currentScanner;
}

export function getAccuscanStrip() {
  return state.currentStrip;
}

export function getAccuscanStrips() {
  return state.strips;
}

export function getInitialAccuscanStrips() {
  return state.initialStrips;
}

export function getScanURL() {
  return state.url;
}


function isHandshaking() {
  return state.resolveScannersRequest && state.resolveScannersRequest !== identity;
}

function resolveDefaultScanners() {
  state.resolveScannersRequest({
    status: true,
    connected: false,
    scanners: ['Manual'],
  });

  state.resolveScannersRequest = identity;
}


function sendMessage(msg) {
  let m = typeof msg === 'string' ? msg : JSON.serialize(msg);

  getSocket()
    .then(socket => {
      socket.send(m);
    })
    .catch(() => {
      if (!testConnectSidebarEnabled) {
        events.publish('error.user', {message: getErrorCode(11002, getCurrentScanner()), key: SCAN_ERROR_KEY});
      }

      if (isHandshaking()) {
        resolveDefaultScanners();
      }
    });
}

let lastCall = new Date();

export function storeHistory(data) {
  if (!testConnectSidebarEnabled || new Date() - lastCall <= 1000) {
    return;
  }

  let jsonData = JSON.stringify(data);

  sendMessage('StoreHistory' + jsonData);
  lastCall = new Date();
}

export function updateHistory(data) {
  if (!testConnectSidebarEnabled) {
    return;
  }

  let jsonData = JSON.stringify(data);

  sendMessage('UpdateHistory' + jsonData);
}

export function getHistory() {
  if (!testConnectSidebarEnabled || isScanning()) {
    return Promise.resolve({});
  }

  sendMessage('GetHistory');

  return new Promise(resolve => {
    state.resolveHistoryRequest = resolve;
  });
}

let lastStatusRequest = {};

export function getStatus() {
  if (isScanning()) {
    return Promise.resolve(lastStatusRequest);
  }

  if (!testConnectSidebarEnabled) {
    return Promise.resolve({});
  }

  sendMessage('GetStatus');

  return new Promise(resolve => {
    state.resolveStatusRequest = data => {
      lastStatusRequest = data;
      resolve(data);
    };
  });
}

/**
 * Returns Promise that resolves with the currently connected scanners.
 * @return {Promise} Resolves with an object containing the current scanners and the metadata.
 */
export function getScanners() {
  sendMessage('Handshake');

  // Return Promise which we resolve when the data comes back in later
  return new Promise(resolve => {
    state.resolveScannersRequest = resolve;
  });
}

export function getInfo() {
  sendMessage('SystemInfo');
  // Return Promise which we resolve when the data comes back in later
  return new Promise(resolve => {
    state.resolveScannersRequest = resolve;
  });
}

function normalizeChemicalBaseTypeForScanners(cbt) {
  if ([13, 15, '13', '15'].includes(cbt)) {
    return 5;
  }

  if ([14, '14'].includes(cbt)) {
    return 2;
  }

  return cbt;
}

export function startAccuscan({strips, chemicalBaseTypeID}) {
  let strip = strips[0];

  sendMessage(`Accuscan ${strip} ${normalizeChemicalBaseTypeForScanners(chemicalBaseTypeID)}`);

  state.strips = strips;
  state.chemicalBaseTypeID = normalizeChemicalBaseTypeForScanners(chemicalBaseTypeID);
  state.currentScanner = 'Accuscan';
  state.initialStrips = strips.length;
  state.currentStrip = strip;
  state.url = window.location.hash;

  events.publish('cancel.error', {key: SCAN_ERROR_KEY});

  events.publish('scanner.start');
}

export function nextAccuscanStrip() {
  state.strips.shift();
  let strip = state.strips[0];

  sendMessage(`Accuscan ${strip} ${state.chemicalBaseTypeID}`);

  state.currentScanner = 'Accuscan';
  state.currentStrip = strip;

  events.publish('scanner.start');
}

export function startAccuscanCalibration() {
  sendMessage('Accuscan CopperCalibration');

  state.currentScanner = 'Accuscan';

  state.url = window.location.hash;

  events.publish('cancel.error', {key: SCAN_ERROR_KEY});

  events.publish('scanner.start');
}

export function startSmartLab({chemicalBaseTypeID, disk}) {
  sendMessage(`SmartLab ${normalizeChemicalBaseTypeForScanners(chemicalBaseTypeID)} ${disk || ''}`);

  state.currentScanner = 'SmartLab';

  state.url = window.location.hash;

  events.publish('cancel.error', {key: SCAN_ERROR_KEY});

  events.publish('scanner.start');
}

export function startSmartLabCalibration() {
  sendMessage('SmartLab Calibration');

  state.currentScanner = 'SmartLab';

  state.url = window.location.hash;

  events.publish('cancel.error', {key: SCAN_ERROR_KEY});

  events.publish('scanner.start');
}


/**
 * Reset shared state to initial values.
 * @param {boolean}      handShake   Whether or not to reinitialize connection with scanner after reset
 * @return {undefined}
 */
export function resetTestConnect(handShake = true) {
  state = Object.assign({}, initialState, {resolveScannersRequest: state.resolveScannersRequest});

  events.publish('scanner.cancel');

  if (handShake) {
    getScanners(); // Handshake resets cached errors in test connect
  }
}


/*
    Socket Handlers
    ================
 */
function dataHandler(message) {
  let data = JSON.parse(message && message.data ? message.data : '');

  if (data && data.history) {
    state.resolveHistoryRequest({
      history: data.history,
    });
    state.resolveHistoryRequest = identity;
    return;
  }

  if (data && data.RequestId === 'GetStatus') {
    state.resolveStatusRequest({
      scanners: (data.scanners || []).concat(['Manual']),
      version: data.version,
      connected: true,
    });
    state.resolveStatusRequest = identity;
    return;
  }

  if (data && data.info) {
    state.resolveScannersRequest({
      name: data.name,
      version: data.version,
      OSVersion: data.OSVersion,
      systemID: data.systemID,
    });
    state.resolveScannersRequest = identity;
    return;
  }

  if (data && data.error) {
    console.error(data);

    if (isHandshaking()) {
      resolveDefaultScanners();
      return;
    } else if (isScanning()) {
      //If an error happens during a scan it will be caught here. Code will be a Lamotte code most likely.

      if (state.currentScanner === 'Accuscan' || !data.code) {
        events.publish('error.user', {message: data.error, key: SCAN_ERROR_KEY});
      } else {
        events.publish('error.user', {message: getErrorCode(data.code, getCurrentScanner()), key: SCAN_ERROR_KEY});
      }

      storeHistory({
        id: 'err',
        scanner: state.currentScanner,
        time: new Date(),
        data: {
          errorMessage: data.code ? getErrorCodeText(data.code, getCurrentScanner()) : data.error,
          key: SCAN_ERROR_KEY,
        },
      });

      setTimeout(function() {
        events.publish('testconnect.historyChanged');
      }, 1000);
    }

    resetTestConnect();
    return;
  }


  if (!!data && !!data.scanners && data.version && !!state.resolveScannersRequest) {
    state.resolveScannersRequest({
      status: true,
      connected: true,
      version: data.version,
      scanners: (data.scanners || []).concat(['Manual']),
      calibrationDate: data.date,
    });

    state.resolveScannersRequest = identity;

    saveVersion({
      version: data.version,
      smartLab: (data.scanners || []).includes('SmartLab'),
      accuScan: (data.scanners || []).includes('Accuscan'),
    });

    return;
  }

  if (data && (data.progress || data.progress === 0)) {
    events.publish('scanner.progress', data.progress);
    return;
  }


  if (data) {
    state.results = data;

    if (state.currentScanner !== 'Accuscan' || getAccuscanStrips().length === 1) {
      state.currentScanner = undefined;
      state.strips = [];
      state.currentStrip = undefined;
      state.initialStrips = 0;
    } else {
      events.publish('scanner.resetting');
    }
    //sets state in external file because of duplication bug
    setState(data);
    events.publish('scanner.complete');
  }
}

function errorHandler(err) {
  if (isHandshaking()) {
    resolveDefaultScanners();
    return;
  }

  if (isScanning()) {
    console.log('socket error', err);
    events.publish('error.user', {message: getErrorCode(11004, getCurrentScanner()), key: SCAN_ERROR_KEY});
  }

  resetTestConnect(false);
}

function connectionClosedHandler() {
  if (isScanning()) { // Only sent message if disconnect during a scan; otherwise doesn't matter
    events.publish('error.user', {message: getErrorCode(11001, getCurrentScanner()), key: SCAN_ERROR_KEY});
    resetTestConnect(); // Don't want to reset data in case scan had previously finished so put in loop
  }
}
